import { Pressable, View } from "react-native";
import { ChannelListSkeleton } from "@/components/Skeleton";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { useChannels, type ChannelWithMembership } from "@/hooks/useChannels";
import { requestChannelJoin } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";

/* ─── styled components ─── */

const Screen = styled.ScrollView`
  flex: 1;
  background-color: ${colors.bg};
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Section = styled.View<{ extraTop?: number }>`
  padding-horizontal: 20px;
  padding-top: ${(p) => (p.extraTop != null ? p.extraTop : 24)}px;
  padding-bottom: 8px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Card = styled.View`
  margin-horizontal: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const EmptyCard = styled.View`
  margin-horizontal: 20px;
  margin-top: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 32px;
  align-items: center;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const Row = styled.View<{ showBorder?: boolean; pressed?: boolean; hasUnread?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  border-bottom-width: ${(p) => (p.showBorder ? 1 : 0)}px;
  border-bottom-color: ${colors.line};
  background-color: ${(p) => (p.pressed ? colors.bg : p.hasUnread ? colors.brand50 : "transparent")};
`;

const IconBox = styled.View<{ member?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${radius.md}px;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  background-color: ${(p) => (p.member ? colors.brand50 : colors.bg)};
  border-width: ${(p) => (p.member ? 0 : 1)}px;
  border-color: ${(p) => (p.member ? "transparent" : colors.line)};
`;

const EmojiText = styled.Text`
  font-size: 18px;
`;

const RowBody = styled.View`
  flex: 1;
  margin-right: 12px;
`;

const RowTop = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ChannelName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const LastMsg = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const RequestBtn = styled.View<{ pressed?: boolean }>`
  border-width: 2px;
  border-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 6px;
  opacity: ${(p) => (p.pressed ? 0.7 : 1)};
`;

const RequestBtnText = styled.Text`
  color: ${colors.brand};
  font-size: 12px;
  font-weight: ${font.bold};
`;

const PendingBadge = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.full}px;
  padding-horizontal: 10px;
  padding-vertical: 4px;
  border-width: 1px;
  border-color: ${colors.amber200};
`;

const PendingText = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const EmptyEmoji = styled.Text`
  font-size: 36px;
  margin-bottom: 12px;
`;

const CreateBtnText = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: #fff;
`;

const Spacer = styled.View`
  height: 32px;
`;

/* ─── components ─── */

function ChannelRow({
  channel,
  clubId,
  isLast,
}: {
  channel: ChannelWithMembership;
  clubId: string;
  isLast: boolean;
}) {
  const { user } = useAuth();
  const router = useRouter();

  const handleRequest = async () => {
    if (!user) return;
    await requestChannelJoin(
      clubId,
      channel.id,
      user.uid,
      user.displayName || user.email?.split("@")[0] || "",
    );
  };

  const handlePress = () => {
    if (channel.isMember) {
      router.push(`/(app)/chat?id=${channel.id}&name=${encodeURIComponent(channel.name)}`);
    }
  };

  return (
    <Pressable onPress={handlePress}>
      {({ pressed }: { pressed: boolean }) => (
        <Row showBorder={!isLast} pressed={pressed} hasUnread={channel.hasUnread}>
          <IconBox member={channel.isMember}>
            <EmojiText>{(channel as any).emoji || "\u{1F4AC}"}</EmojiText>
          </IconBox>
          <RowBody>
            <RowTop>
              <ChannelName style={channel.hasUnread ? { fontWeight: "800" } : undefined}>{channel.name}</ChannelName>
            </RowTop>
            {channel.lastMessage && (
              <LastMsg numberOfLines={1}>{channel.lastMessage}</LastMsg>
            )}
          </RowBody>
          {channel.hasUnread && (
            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: colors.brand, marginLeft: 8 }} />
          )}
          {!channel.isMember && !channel.isPending && (
            <Pressable onPress={handleRequest}>
              {({ pressed: btnPressed }: { pressed: boolean }) => (
                <RequestBtn pressed={btnPressed}>
                  <RequestBtnText>Request</RequestBtnText>
                </RequestBtn>
              )}
            </Pressable>
          )}
          {channel.isPending && (
            <PendingBadge>
              <PendingText>Pending</PendingText>
            </PendingBadge>
          )}
        </Row>
      )}
    </Pressable>
  );
}

export default function ChannelsScreen() {
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const router = useRouter();
  const { channels, loading } = useChannels(club?.id, user?.uid);

  const yours = channels.filter((c) => c.isMember);
  const other = channels.filter((c) => !c.isMember);
  const canCreate = membership?.role === "admin" || isAnyCaptain(user?.uid);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <ChannelListSkeleton />
      </View>
    );
  }

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth style={{ paddingTop: 20 }}>
        {canCreate && (
          <Pressable
            onPress={() => router.push("/(app)/create-channel")}
            style={({ pressed }) => ({
              marginHorizontal: 20,
              marginBottom: 16,
              backgroundColor: colors.brand,
              borderRadius: radius.card,
              paddingVertical: 14,
              alignItems: "center" as const,
              opacity: pressed ? 0.9 : 1,
            })}
          >
            <CreateBtnText>+ New channel</CreateBtnText>
          </Pressable>
        )}

        {yours.length > 0 && (
          <>
            <Section>
              <SectionLabel>Your channels</SectionLabel>
            </Section>
            <Card>
              {yours.map((c, i) => (
                <ChannelRow
                  key={c.id}
                  channel={c}
                  clubId={club?.id || ""}
                  isLast={i === yours.length - 1}
                />
              ))}
            </Card>
          </>
        )}

        {other.length > 0 && (
          <>
            <Section extraTop={yours.length > 0 ? 28 : 0}>
              <SectionLabel>Discover</SectionLabel>
            </Section>
            <Card>
              {other.map((c, i) => (
                <ChannelRow
                  key={c.id}
                  channel={c}
                  clubId={club?.id || ""}
                  isLast={i === other.length - 1}
                />
              ))}
            </Card>
          </>
        )}

        {channels.length === 0 && (
          <EmptyCard>
            <EmptyEmoji>{"\u{1F4AC}"}</EmptyEmoji>
            <EmptyText>No channels yet.</EmptyText>
          </EmptyCard>
        )}

        <Spacer />
      </ContentWidth>
    </Screen>
  );
}
